import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BlogPostStatus,
  deleteBlogPost,
  listBlogCategories,
  listBlogPosts,
  publishBlogPost,
  unpublishBlogPost,
} from '../api/blog';
import type { BlogCategory, BlogPostStatusValue, BlogPostSummary } from '../api/blog';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BlogCategoryModal } from './blog/BlogCategoryModal';
import { BlogSummary } from './blog/BlogSummary';
import { CoverThumbnail, formatDateTime, postStatusTag } from './blog/blogDisplay';

/** Bộ lọc tình trạng. */
type StatusFilter = 'all' | 'published' | 'draft';

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly BlogPostSummary[];
  readonly totalCount: number;
}

const pageSize = 20;

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'published', label: 'Đã đăng' },
  { value: 'draft', label: 'Nháp' },
];

const statusValues: Readonly<Record<Exclude<StatusFilter, 'all'>, BlogPostStatusValue>> = {
  published: BlogPostStatus.Published,
  draft: BlogPostStatus.Draft,
};

/** Đường dẫn trang sửa một bài. */
function editRoute(id: string): string {
  return AppRoutes.BlogEditPattern.replace(':postId', id);
}

/**
 * Trang "Blog" — danh sách bài viết trên blog của website công ty.
 *
 * Viết và sửa bài mở ở một trang riêng (nội dung bài dài, cần cả màn hình). Danh mục quản lý trong một
 * cửa sổ nhỏ ngay tại đây.
 */
export function BlogPage(): ReactNode {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.BlogManage);

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [categories, setCategories] = useState<readonly BlogCategory[] | null>(null);
  const [categoryToken, setCategoryToken] = useState(0);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, categoryId, status, pageNumber, reloadToken }),
    [searchTerm, categoryId, status, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const page = await listBlogPosts({
          searchTerm,
          pageNumber,
          pageSize,
          ...(categoryId === null ? {} : { categoryId }),
          ...(status === 'all' ? {} : { status: statusValues[status] }),
        });

        if (isCurrent) {
          setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
        }
      } catch (error) {
        if (isCurrent) {
          setErrorMessage(error instanceof ApiError ? error.message : 'Không tải được danh sách bài viết.');
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [requestKey, searchTerm, categoryId, status, pageNumber]);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const result = await listBlogCategories();

        if (isCurrent) {
          setCategories(result);
        }
      } catch {
        // Thiếu danh mục thì bộ lọc danh mục trống; danh sách bài vẫn dùng được.
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [categoryToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
    setCategoryToken((token) => token + 1);
  }, []);

  /**
   * Bật là đăng bài, giữ ngày đăng cũ nếu có — bài từng đăng rồi tắt thì hiện lại đúng chỗ cũ trên
   * website, không nhảy lên đầu như bài mới. Tắt là gỡ về nháp; nội dung và ngày đăng vẫn giữ.
   */
  async function togglePublished(post: BlogPostSummary, isPublished: boolean): Promise<void> {
    try {
      if (isPublished) {
        const published = await publishBlogPost(post.id, null);

        message.success(
          published.isScheduled
            ? `Đã bật — bài tự hiện trên website lúc ${formatDateTime(published.publishedAt)}.`
            : 'Đã hiện lên website.',
        );
      } else {
        await unpublishBlogPost(post.id);
        message.success('Đã ẩn khỏi website, bài về nháp.');
      }

      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được.');
    }
  }

  async function remove(post: BlogPostSummary): Promise<void> {
    try {
      await deleteBlogPost(post.id);
      message.success('Đã xóa bài viết.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  const columns: TableColumnsType<BlogPostSummary> = [
    {
      title: 'Bài viết',
      key: 'post',
      render: (_, post) => (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <CoverThumbnail url={post.coverImageUrl} width={112} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography.Paragraph
              strong
              style={{ marginBottom: 2 }}
              ellipsis={{ rows: 2, tooltip: post.title }}
            >
              <Typography.Link onClick={() => { void navigate(editRoute(post.id)); }} style={{ color: 'inherit' }}>
                {post.title}
              </Typography.Link>
            </Typography.Paragraph>
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: post.excerpt }}>
              {post.excerpt === '' ? `/blog/${post.slug}` : post.excerpt}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'categoryName',
      width: 160,
      render: (categoryName: string | null) =>
        categoryName === null ? (
          <Typography.Text type="secondary">Chưa phân loại</Typography.Text>
        ) : (
          <Tag bordered={false} color="purple" style={{ marginInlineEnd: 0 }}>
            {categoryName}
          </Tag>
        ),
    },
    {
      title: 'Tình trạng',
      key: 'status',
      width: 170,
      render: (_, post) => (
        <div>
          {postStatusTag(post)}
          {post.publishedAt === null ? null : (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatDateTime(post.publishedAt)}
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tác giả',
      dataIndex: 'authorName',
      width: 150,
      render: (authorName: string | null) =>
        authorName ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Sửa lần cuối',
      dataIndex: 'updatedAt',
      width: 140,
      render: (updatedAt: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatDateTime(updatedAt)}
        </Typography.Text>
      ),
    },
    {
      title: 'Hiện trên web',
      key: 'isPublished',
      width: 120,
      align: 'center',
      render: (_, post) => {
        const isPublished = post.status === BlogPostStatus.Published;

        return (
          <Tooltip
            title={
              !isPublished
                ? 'Đang ẩn khỏi website'
                : post.isScheduled
                  ? `Hẹn giờ: tự hiện lúc ${formatDateTime(post.publishedAt)}`
                  : 'Đang hiện trên website'
            }
          >
            <Switch
              size="small"
              checked={isPublished}
              disabled={!canManage}
              onChange={(checked) => void togglePublished(post, checked)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 92,
      align: 'center',
      fixed: 'right',
      render: (_, post) => (
        <Space size={2}>
          <Tooltip title={canManage ? 'Sửa' : 'Xem'}>
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa"
              onClick={() => { void navigate(editRoute(post.id)); }}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa bài viết này?"
            description="Bài sẽ biến mất khỏi website. Muốn tạm gỡ thì tắt công tắc Hiện trên web."
            okText="Xóa"
            cancelText="Không"
            okButtonProps={{ danger: true }}
            disabled={!canManage}
            onConfirm={() => { void remove(post); }}
          >
            <Tooltip title="Xóa">
              <Button type="text" danger icon={<DeleteOutlined />} aria-label="Xóa" disabled={!canManage} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Blog
          </Typography.Title>
          <Typography.Text type="secondary">
            Bài viết trên blog của website công ty. Bài đã đăng hiện trên mvalab.vn sau khoảng một phút.
          </Typography.Text>
        </div>
        <Space wrap>
          <Button icon={<EyeOutlined />} onClick={() => { void navigate(AppRoutes.BlogPreview); }}>
            Xem như trên website
          </Button>
          <Button icon={<TagsOutlined />} onClick={() => { setIsCategoryOpen(true); }}>
            Danh mục
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => { void navigate(AppRoutes.BlogNew); }}
          >
            Viết bài
          </Button>
        </Space>
      </div>

      <BlogSummary reloadToken={reloadToken} categoryCount={categories?.length} />

      <Card size="small" styles={{ body: { padding: 12 } }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input.Search
            allowClear
            placeholder="Tìm theo tiêu đề hoặc tóm tắt"
            style={{ flex: '1 1 220px', maxWidth: 360 }}
            onSearch={(value) => {
              setSearchTerm(value);
              setPageNumber(1);
            }}
          />
          <Select<string>
            allowClear
            placeholder="Danh mục"
            style={{ width: 200 }}
            options={(categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
            onChange={(value: string | undefined) => {
              // Bấm nút xóa lựa chọn thì thư viện gọi lại với undefined.
              setCategoryId(value ?? null);
              setPageNumber(1);
            }}
          />
          <Segmented<StatusFilter>
            options={statusOptions}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPageNumber(1);
            }}
          />
          <Tooltip title="Tải lại">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Tải lại"
              onClick={reload}
              style={{ marginInlineStart: 'auto' }}
            />
          </Tooltip>
        </div>
      </Card>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      <Table<BlogPostSummary>
        rowKey="id"
        columns={columns}
        dataSource={loaded?.items}
        loading={isLoading}
        tableLayout="fixed"
        scroll={{ x: 1120 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={canManage ? 'Chưa có bài viết nào. Bấm “Viết bài” để bắt đầu.' : 'Chưa có bài viết nào.'}
            />
          ),
        }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: loaded?.totalCount ?? 0,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: setPageNumber,
          showTotal: (total) => `${total.toString()} bài viết`,
        }}
      />

      <BlogCategoryModal
        open={isCategoryOpen}
        categories={categories ?? []}
        canManage={canManage}
        onClose={() => { setIsCategoryOpen(false); }}
        onChanged={() => { setCategoryToken((token) => token + 1); }}
      />
    </Space>
  );
}
